import { createFileRoute, Link, Outlet, notFound, useMatches } from "@tanstack/react-router";
import { LOCATIONS, type Location } from "@/content/locations";
import { buildSeo } from "@/lib/seo";
import { isPairedCity } from "@/content/language-pairs";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RangpurSeoDetails } from "@/components/RangpurSeoDetails";
import { CityHubStructuredData } from "@/components/city-hub/CityHubStructuredData";
import { CityHubHero } from "@/components/city-hub/CityHubHero";
import {
  CityHubCoverage,
  CityHubFaq,
  CityHubMap,
  CityHubOpportunity,
  CityHubResources,
  CityHubWhy,
} from "@/components/city-hub/CityHubSections";
import {
  CityHubNearby,
  CityHubRangpurAreas,
  CityHubReading,
  CityHubServicePages,
} from "@/components/city-hub/CityHubLinks";
import { CityHubCta } from "@/components/city-hub/CityHubCta";

/**
 * City hub at /seo-services/$location. Sections live in src/components/city-hub;
 * Rangpur additionally renders its dedicated local guidance and area links.
 */
export const Route = createFileRoute("/seo-services/$location")({
  loader: ({ params }): { loc: Location } => {
    const loc = LOCATIONS.find((l) => l.slug === params.location);
    if (!loc) throw notFound();
    return { loc };
  },
  head: ({ loaderData, match, matches }) => {
    // This route is also the layout parent of /seo-services/$location/$service.
    // Emitting its head on a child match would ship a second canonical/OG set,
    // so only the leaf match contributes metadata.
    const isLeaf = matches[matches.length - 1]?.routeId === match.routeId;
    if (!isLeaf) return {};
    const loc = loaderData?.loc;
    if (!loc) return { meta: [{ title: "Location not found" }] };

    return buildSeo({
      title:
        loc.slug === "rangpur"
          ? "SEO Expert in Rangpur — Rahatul Ashiq Tamal"
          : `SEO Expert in ${loc.city} — Local SEO | Freelancer Tamal`,
      description:
        loc.slug === "rangpur"
          ? "Work with Rahatul Ashiq Tamal, an SEO expert based in Rangpur. Local SEO, Google Business Profile, technical SEO and Bangla content. Explore scope and pricing."
          : `Local SEO, Google Business Profile, technical SEO & content for ${loc.city}, ${loc.country} businesses. Real results, transparent reporting.`,
      path: `/seo-services/${loc.slug}`,
      keywords: `SEO expert ${loc.city}, SEO services ${loc.city}, local SEO ${loc.city}, ${loc.city} SEO consultant, ${loc.city} digital marketing, freelance SEO ${loc.city}`,
      ogEyebrow: `Local SEO · ${loc.country}`,
      ogTitle: `SEO Expert in ${loc.city}`,
      ...(isPairedCity(loc.slug) ? { alternates: { bn: `/bn/seo-services/${loc.slug}` } } : {}),
    });
  },
  component: LocationPage,
  notFoundComponent: () => (
    <div className="container-x py-32 text-center">
      <h1 className="font-display text-3xl">Location not found</h1>
      <Link to="/services" className="mt-6 inline-flex text-primary">
        Back to services
      </Link>
    </div>
  ),
});

function LocationPage() {
  const { loc } = Route.useLoaderData();
  const matches = useMatches();
  // This route is also the layout parent of /seo-services/$location/$service.
  // When a child match is active, render it instead of the city hub — otherwise
  // the service page would never appear.
  const isLeaf = matches[matches.length - 1]?.routeId === "/seo-services/$location";
  if (!isLeaf) return <Outlet />;
  const isRangpur = loc.slug === "rangpur";

  return (
    <>
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "SEO Locations", path: "/seo-locations" },
          { name: loc.city, path: `/seo-services/${loc.slug}` },
        ]}
      />
      <CityHubStructuredData loc={loc} />
      <CityHubHero loc={loc} />
      {isRangpur && <RangpurSeoDetails />}
      <CityHubWhy loc={loc} />
      <CityHubCoverage loc={loc} />
      <CityHubOpportunity loc={loc} />
      <CityHubResources loc={loc} />
      <CityHubMap loc={loc} />
      <CityHubFaq loc={loc} />
      <CityHubNearby loc={loc} />
      <CityHubReading loc={loc} />
      <CityHubServicePages loc={loc} />
      {isRangpur && <CityHubRangpurAreas />}
      <CityHubCta loc={loc} />
    </>
  );
}
